import { useEffect, useState } from 'react';
import { ArrowLeft, Bot, Pencil, Plug, Plus, Trash2, UsersRound } from 'lucide-react';
import { useAiProviders } from '@/api/ai';
import {
  useAddAiSkill,
  useAiAgents,
  useDeleteAiAgent,
  useRemoveAiSkill,
  useSaveAiAgent,
  useTestMcp,
  useUpdateAiSkill,
} from '@/api/ai-agents';
import type { AiAgent, AiSkill, McpTestResult } from '@/api/types';
import { ApiError } from '@/api/client';
import {
  Badge,
  Button,
  Card,
  Checkbox,
  EmptyState,
  ErrorState,
  Input,
  Modal,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
} from '@/components/ui';
import { cn } from '@/lib/cn';
import { UseCasesPanel } from './UseCasesPanel';
import { SkillTools } from './SkillTools';

type View = { mode: 'list' } | { mode: 'edit'; id: string } | { mode: 'new' };

export function AiAgentsPage() {
  const agents = useAiAgents();
  const [view, setView] = useState<View>({ mode: 'list' });
  const list = agents.data ?? [];
  const selected = view.mode === 'edit' ? (list.find((a) => a.id === view.id) ?? null) : null;

  useEffect(() => {
    if (view.mode === 'edit' && !agents.isPending && agents.data && !selected) {
      setView({ mode: 'list' });
    }
  }, [view, agents.isPending, agents.data, selected]);

  if (view.mode !== 'list') {
    return (
      <AgentEditor
        key={view.mode === 'edit' ? view.id : 'new'}
        agent={selected}
        others={list}
        onBack={() => setView({ mode: 'list' })}
        onCreated={(id) => setView({ mode: 'edit', id })}
      />
    );
  }

  return (
    <>
      <PageHeader
        title="AI agents"
        description="Create AI agents for your chat widgets, and give each one skills — your own backend servers — so it can look up orders, accounts and anything else it needs before replying."
        actions={
          <Button onClick={() => setView({ mode: 'new' })} disabled={agents.isPending}>
            <Plus className="h-4 w-4" /> New agent
          </Button>
        }
      />

      {agents.isPending ? (
        <Skeleton className="h-96 rounded-2xl" />
      ) : agents.error ? (
        <Card>
          <ErrorState error={agents.error} onRetry={() => void agents.refetch()} />
        </Card>
      ) : list.length === 0 ? (
        <Card>
          <EmptyState
            icon={<Bot className="h-6 w-6" />}
            title="No AI agent yet"
            description="Create one, write its instructions, and connect your own backend as a skill."
            action={<Button onClick={() => setView({ mode: 'new' })}>Create agent</Button>}
          />
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {list.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setView({ mode: 'edit', id: a.id })}
              className="group w-full text-left"
            >
              <Card className="hover:border-brand-200 h-full p-5 transition-all hover:-translate-y-0.5 hover:shadow-lg">
                <div className="flex items-start gap-3">
                  <span className="from-brand-500 to-brand-700 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm">
                    <Bot className="h-5 w-5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-slate-900">{a.name}</p>
                    <p className="mt-0.5 line-clamp-2 text-sm text-slate-500">
                      {a.description || 'No description yet'}
                    </p>
                  </div>
                  {!a.isEnabled && <Badge>Off</Badge>}
                </div>
                <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-500">
                  <Badge tone={a.skills.length ? 'brand' : undefined}>
                    {a.skills.length
                      ? `${a.skills.length} skill${a.skills.length === 1 ? '' : 's'}`
                      : 'No skills'}
                  </Badge>
                  {a.collaboratorIds?.length > 0 && (
                    <Badge>
                      {a.collaboratorIds.length} collaborator{a.collaboratorIds.length === 1 ? '' : 's'}
                    </Badge>
                  )}
                  {a.useKnowledgeBase && <Badge>Help articles</Badge>}
                </div>
              </Card>
            </button>
          ))}
        </div>
      )}
      <UseCasesPanel />
    </>
  );
}

type AgentForm = {
  name: string;
  description: string;
  isEnabled: boolean;
  instructions: string;
  providerId: string | null;
  useKnowledgeBase: boolean;
  collaboratorIds: string[];
};

const formFrom = (a: AiAgent | null): AgentForm => ({
  name: a?.name ?? '',
  description: a?.description ?? '',
  isEnabled: a?.isEnabled ?? true,
  instructions: a?.instructions ?? '',
  providerId: a?.providerId ?? null,
  useKnowledgeBase: a?.useKnowledgeBase ?? true,
  collaboratorIds: a?.collaboratorIds ?? [],
});

const MAX_COLLABORATORS = 10;

function AgentEditor({
  agent,
  others,
  onBack,
  onCreated,
}: {
  agent: AiAgent | null;
  others: AiAgent[];
  onBack: () => void;
  onCreated: (id: string) => void;
}) {
  const toast = useToast();
  const save = useSaveAiAgent();
  const remove = useDeleteAiAgent();
  const providers = useAiProviders();
  const [form, setForm] = useState<AgentForm>(formFrom(agent));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [editingSkill, setEditingSkill] = useState<AiSkill | 'new' | null>(null);
  useEffect(() => setForm(formFrom(agent)), [agent]);

  const set = <K extends keyof AgentForm>(k: K, v: AgentForm[K]) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = agent ? JSON.stringify(form) !== JSON.stringify(formFrom(agent)) : true;
  const valid = form.name.trim().length >= 2;

  const persist = () => {
    const input = {
      name: form.name.trim(),
      description: form.description.trim(),
      isEnabled: form.isEnabled,
      instructions: form.instructions.trim(),
      providerId: form.providerId,
      useKnowledgeBase: form.useKnowledgeBase,
      collaboratorIds: form.collaboratorIds,
    };
    save.mutate(agent ? { id: agent.id, input } : { input }, {
      onSuccess: (a) => {
        toast.success(agent ? 'Agent saved' : 'AI agent created');
        if (!agent) onCreated(a.id);
      },
      onError: (e) => {
        const fields = e instanceof ApiError ? e.fieldErrors : {};
        toast.error(Object.values(fields)[0] ?? e.message);
      },
    });
  };

  return (
    <>
      <div className="mb-4 flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={onBack}>
          <ArrowLeft className="h-4 w-4" /> All agents
        </Button>
        {!agent && <span className="text-sm text-slate-400">New agent — not saved yet</span>}
      </div>

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_360px]">
        <Card className="min-w-0 overflow-hidden">
          <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-5 py-4">
            <span className="from-brand-500 to-brand-700 flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br text-white shadow-sm">
              <Bot className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate font-semibold text-slate-900">{form.name.trim() || 'New agent'}</p>
              <p className="text-xs text-slate-500">
                {agent
                  ? agent.skills.length
                    ? `${agent.skills.length} skill${agent.skills.length === 1 ? '' : 's'}`
                    : 'No skills yet'
                  : 'Fill in the details to create this agent'}
              </p>
            </div>
            <Checkbox
              label={form.isEnabled ? 'Live' : 'Off'}
              checked={form.isEnabled}
              onChange={(e) => set('isEnabled', e.target.checked)}
            />
          </div>

          <div className="space-y-5 p-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                hint="For your team — visitors never see this."
              />
              <Select
                label="Model provider"
                hint="Defaults to the workspace provider."
                value={form.providerId ?? ''}
                onChange={(e) => set('providerId', e.target.value || null)}
              >
                <option value="">Workspace default</option>
                {(providers.data ?? [])
                  .filter((p) => p.isEnabled)
                  .map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} · {p.model}
                    </option>
                  ))}
              </Select>
            </div>
            <Input
              label="Description"
              placeholder="Answers order and delivery questions"
              hint="Optional. Shows on the agent picker in the chat widget settings."
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />
            <Checkbox
              label="Search help articles"
              description="Let this agent quote your knowledge base before calling tools"
              checked={form.useKnowledgeBase}
              onChange={(e) => set('useKnowledgeBase', e.target.checked)}
            />
            <Textarea
              label="Instructions"
              rows={6}
              placeholder="Persona, tone and things to always or never do. E.g. “Always check the order status before answering delivery questions.”"
              hint="Added on top of the workspace AI instructions. Keep secrets out — visitors may see anything you write here."
              value={form.instructions}
              onChange={(e) => set('instructions', e.target.value)}
            />

            {agent ? (
              <SkillsSection
                agent={agent}
                onAdd={() => setEditingSkill('new')}
                onEdit={(s) => setEditingSkill(s)}
              />
            ) : (
              <section className="space-y-3 rounded-2xl border border-slate-200/80 p-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <Plug className="h-4 w-4 text-slate-400" /> Skills
                </h3>
                <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
                  Create the agent first — then come back here to add your backend servers as skills.
                </p>
              </section>
            )}

            <section className="space-y-3 rounded-2xl border border-slate-200/80 p-5">
              <div>
                <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                  <UsersRound className="h-4 w-4 text-slate-400" /> Can ask other agents
                </h3>
                <p className="mt-0.5 text-xs text-slate-500">
                  When this agent hits a question it cannot verify on its own, it can consult these agents —
                  they check with their own skills and hand back findings. Used during customer chats only.
                </p>
              </div>
              {others.filter((a) => a.id !== agent?.id).length === 0 ? (
                <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
                  Create another agent first — then they can help each other here.
                </p>
              ) : (
                <div className="space-y-2">
                  {others
                    .filter((a) => a.id !== agent?.id)
                    .map((a) => {
                      const checked = form.collaboratorIds.includes(a.id);
                      return (
                        <Checkbox
                          key={a.id}
                          checked={checked}
                          disabled={!checked && form.collaboratorIds.length >= MAX_COLLABORATORS}
                          label={
                            <>
                              {a.name}
                              {!a.isEnabled && <span className="ml-1 text-xs text-slate-400">(off)</span>}
                            </>
                          }
                          description={a.description || undefined}
                          onChange={(e) =>
                            set(
                              'collaboratorIds',
                              e.target.checked
                                ? [...form.collaboratorIds, a.id]
                                : form.collaboratorIds.filter((id) => id !== a.id),
                            )
                          }
                        />
                      );
                    })}
                </div>
              )}
            </section>
          </div>

          <div className="flex items-center gap-2 border-t border-slate-100 bg-slate-50/60 px-5 py-3">
            {agent && (
              <Button
                variant={confirmDelete ? 'danger' : 'ghost'}
                size="sm"
                loading={remove.isPending}
                onClick={() =>
                  confirmDelete
                    ? remove.mutate(agent.id, {
                        onSuccess: (r) => {
                          toast.success(
                            r.unlinkedWidgets
                              ? `Agent deleted — ${r.unlinkedWidgets} widget${r.unlinkedWidgets === 1 ? '' : 's'} unlinked`
                              : 'Agent deleted',
                          );
                          onBack();
                        },
                        onError: (e) => toast.error(e.message),
                      })
                    : setConfirmDelete(true)
                }
              >
                {confirmDelete ? 'Confirm delete' : 'Delete agent'}
              </Button>
            )}
            <span className="ml-auto text-xs text-slate-400">
              {dirty ? 'Unsaved changes' : 'All changes saved'}
            </span>
            <Button size="sm" disabled={!dirty || !valid} loading={save.isPending} onClick={persist}>
              {agent ? 'Save changes' : 'Create agent'}
            </Button>
          </div>
        </Card>

        <Card className="h-fit p-5">
          <h3 className="text-sm font-semibold text-slate-900">How skills work</h3>
          <ul className="mt-3 space-y-3 text-sm text-slate-600">
            <li>
              A skill is an <span className="font-medium text-slate-800">MCP server</span> you run on your own
              backend (for example an <code className="text-xs">/mcp</code> endpoint that can look up orders).
            </li>
            <li>
              When a customer chats, the agent can call the tools you enable here with the customer’s
              question, then answer with real data.
            </li>
            <li>
              Auth values are encrypted and never shown again — paste a key that can only read what the agent
              needs.
            </li>
            <li>No skill yet? The agent still replies using its instructions and your help articles.</li>
          </ul>
        </Card>
      </div>

      {editingSkill && agent && (
        <SkillModal agent={agent} skill={editingSkill} onClose={() => setEditingSkill(null)} />
      )}
    </>
  );
}

function SkillsSection({
  agent,
  onAdd,
  onEdit,
}: {
  agent: AiAgent;
  onAdd: () => void;
  onEdit: (skill: AiSkill) => void;
}) {
  const toast = useToast();
  const update = useUpdateAiSkill();

  return (
    <section className="space-y-3 rounded-2xl border border-slate-200/80 p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="flex items-center gap-2 text-sm font-semibold text-slate-900">
            <Plug className="h-4 w-4 text-slate-400" /> Skills
          </h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Your own backend servers (MCP) this agent may call while answering.
          </p>
        </div>
        <Button variant="secondary" size="sm" onClick={onAdd}>
          <Plus className="h-3.5 w-3.5" /> Add skill
        </Button>
      </div>
      {agent.skills.length === 0 ? (
        <p className="rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-500">
          No skills yet — the agent will answer from its instructions and help articles only.
        </p>
      ) : (
        <div className="space-y-2">
          {agent.skills.map((s) => (
            <div
              key={s.id}
              className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 px-4 py-3"
            >
              <Checkbox
                label={<span className="sr-only">Enable {s.name}</span>}
                checked={s.isEnabled}
                onChange={(e) =>
                  update.mutate(
                    { agentId: agent.id, skillId: s.id, input: { isEnabled: e.target.checked } },
                    { onError: (err) => toast.error(err.message) },
                  )
                }
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium text-slate-900">{s.name}</p>
                <p className="truncate font-mono text-xs text-slate-500">{s.url}</p>
              </div>
              {s.hasAuthValue && <Badge tone="green">Auth · {s.authValueLast4}</Badge>}
              <Badge>{s.enabledTools === null ? 'All tools' : `${s.enabledTools.length} tools`}</Badge>
              <Button variant="ghost" size="sm" onClick={() => onEdit(s)} aria-label={`Edit ${s.name}`}>
                <Pencil className="h-3.5 w-3.5" /> Edit
              </Button>
              <SkillTools agent={agent} skill={s} />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

type SkillDraft = {
  name: string;
  url: string;
  authHeader: string;
  authValue: string;
  enabledTools: string[] | null;
};

function SkillModal({
  agent,
  skill,
  onClose,
}: {
  agent: AiAgent;
  skill: AiSkill | 'new';
  onClose: () => void;
}) {
  const toast = useToast();
  const add = useAddAiSkill();
  const update = useUpdateAiSkill();
  const remove = useRemoveAiSkill();
  const test = useTestMcp();
  const saved = skill === 'new' ? null : skill;
  const [form, setForm] = useState<SkillDraft>({
    name: saved?.name ?? '',
    url: saved?.url ?? '',
    authHeader: saved?.authHeader ?? 'Authorization',
    authValue: '',
    enabledTools: saved?.enabledTools ?? null,
  });
  const [result, setResult] = useState<McpTestResult | null>(null);
  const [checked, setChecked] = useState<Set<string> | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const set = <K extends keyof SkillDraft>(k: K, v: SkillDraft[K]) => setForm((f) => ({ ...f, [k]: v }));

  const runTest = () =>
    test.mutate(
      {
        ...(saved ? { agentId: agent.id, skillId: saved.id } : {}),
        url: form.url.trim(),
        authHeader: form.authHeader.trim(),
        ...(form.authValue ? { authValue: form.authValue } : {}),
      },
      {
        onSuccess: (r) => {
          setResult(r);
          if (!r.ok) return;
          const names = r.tools.map((t) => t.name);
          // Keep the previous selection, dropping tools the server no longer exposes.
          setChecked((c) => {
            const prev = c ?? new Set(form.enabledTools ?? names);
            return new Set(names.filter((n) => prev.has(n)));
          });
        },
        onError: (e) => {
          const fields = e instanceof ApiError ? e.fieldErrors : {};
          setResult({ ok: false, message: Object.values(fields)[0] ?? e.message, tools: [] });
        },
      },
    );

  // A saved skill's tools are listed straight away so they can be picked without a manual test.
  useEffect(() => {
    if (saved) runTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const enabledTools = () => {
    if (!result?.ok || !checked) return form.enabledTools ?? undefined;
    return checked.size === result.tools.length ? null : [...checked];
  };

  const submit = () => {
    const input = {
      name: form.name.trim(),
      url: form.url.trim(),
      authHeader: form.authHeader.trim(),
      ...(form.authValue ? { authValue: form.authValue } : {}),
      enabledTools: enabledTools(),
    };
    if (saved) {
      update.mutate(
        { agentId: agent.id, skillId: saved.id, input },
        {
          onSuccess: () => (toast.success('Skill updated'), onClose()),
          onError: (e) => {
            const fields = e instanceof ApiError ? e.fieldErrors : {};
            toast.error(Object.values(fields)[0] ?? e.message);
          },
        },
      );
    } else {
      add.mutate(
        {
          agentId: agent.id,
          input: {
            name: input.name,
            url: input.url,
            ...(form.authHeader.trim() ? { authHeader: form.authHeader.trim() } : {}),
            ...(form.authValue ? { authValue: form.authValue } : {}),
            ...(input.enabledTools !== undefined ? { enabledTools: input.enabledTools } : {}),
          },
        },
        {
          onSuccess: () => (toast.success('Skill added'), onClose()),
          onError: (e) => {
            const fields = e instanceof ApiError ? e.fieldErrors : {};
            toast.error(Object.values(fields)[0] ?? e.message);
          },
        },
      );
    }
  };

  const pending = add.isPending || update.isPending;

  return (
    <Modal
      open
      onClose={onClose}
      title={saved ? `Edit skill — ${saved.name}` : 'Add a skill'}
      description="Point the agent at an MCP server on your backend, test the connection, then pick the tools it may use."
      size="lg"
      footer={
        <div className="flex w-full items-center gap-2">
          {saved && (
            <Button
              variant={confirmDelete ? 'danger' : 'ghost'}
              size="sm"
              loading={remove.isPending}
              onClick={() =>
                confirmDelete
                  ? remove.mutate(
                      { agentId: agent.id, skillId: saved.id },
                      {
                        onSuccess: () => (toast.success('Skill removed'), onClose()),
                        onError: (e) => toast.error(e.message),
                      },
                    )
                  : setConfirmDelete(true)
              }
            >
              <Trash2 className="h-3.5 w-3.5" /> {confirmDelete ? 'Confirm remove' : 'Remove skill'}
            </Button>
          )}
          <Button
            size="sm"
            className="ml-auto"
            variant="secondary"
            loading={test.isPending}
            disabled={!form.url.trim()}
            onClick={runTest}
          >
            Test connection
          </Button>
          <Button
            size="sm"
            loading={pending}
            disabled={form.name.trim().length < 2 || !form.url.trim()}
            onClick={submit}
          >
            {saved ? 'Save skill' : 'Add skill'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Input
            label="Skill name"
            placeholder="Orders"
            hint="Used to prefix tool names, e.g. orders_check_order."
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
          <Input
            label="Auth header"
            placeholder="Authorization"
            value={form.authHeader}
            onChange={(e) => set('authHeader', e.target.value)}
          />
        </div>
        <Input
          label="MCP server URL"
          placeholder="https://api.example.com/mcp"
          className="font-mono text-[13px]"
          value={form.url}
          onChange={(e) => set('url', e.target.value)}
        />
        <Input
          label="Auth header value"
          type="password"
          autoComplete="off"
          placeholder={
            saved?.hasAuthValue ? `•••• ${saved.authValueLast4} — leave blank to keep` : 'Bearer …'
          }
          hint="Encrypted on the server and never shown again."
          value={form.authValue}
          onChange={(e) => set('authValue', e.target.value)}
        />

        {result && (
          <div
            className={cn(
              'rounded-xl p-3 text-sm ring-1',
              result.ok
                ? 'bg-emerald-50 text-emerald-900 ring-emerald-200'
                : 'bg-rose-50 text-rose-900 ring-rose-200',
            )}
          >
            {result.message}
          </div>
        )}

        {test.isPending && !result && <p className="text-sm text-slate-500">Loading tools…</p>}

        {result?.ok && result.tools.length === 0 && (
          <p className="text-sm text-slate-500">This MCP server does not expose any tools yet.</p>
        )}

        {result?.ok && result.tools.length > 0 && (
          <div className="rounded-xl border border-slate-200/80 p-4">
            <div className="flex items-start justify-between gap-3">
              <p className="text-sm font-medium text-slate-800">
                Tools this agent may call{' '}
                <span className="font-normal text-slate-500">
                  ({checked?.size ?? 0} of {result.tools.length})
                </span>
              </p>
              <div className="flex shrink-0 gap-3 text-xs font-medium">
                <button
                  type="button"
                  className="text-violet-600 hover:text-violet-700"
                  onClick={() => setChecked(new Set(result.tools.map((t) => t.name)))}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-slate-500 hover:text-slate-700"
                  onClick={() => setChecked(new Set())}
                >
                  Clear
                </button>
              </div>
            </div>
            <p className="mb-3 text-xs text-slate-500">
              Uncheck anything the agent should not be able to do.{' '}
              {checked?.size === result.tools.length ? 'All tools allowed.' : ''}
            </p>
            <div className="space-y-2">
              {result.tools.map((t) => (
                <Checkbox
                  key={t.name}
                  checked={checked?.has(t.name) ?? false}
                  label={t.name}
                  description={t.description || undefined}
                  onChange={(e) =>
                    setChecked((c) => {
                      const next = new Set(c ?? []);
                      if (e.target.checked) next.add(t.name);
                      else next.delete(t.name);
                      return next;
                    })
                  }
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
